"use client"

import { useRef, useState } from "react"
import { useTranslations } from "next-intl"

import { cn } from "@/lib/utils"

export type DataTableSelectionAction<TData> = {
  label: string
  onAction: (rows: TData[]) => Promise<void> | void
  loading?: boolean
  /** 当前勾选不满足操作条件时置灰（如「分组不能全删」） */
  disabled?: boolean

  variant?: "default" | "destructive"
}

interface DataTableRowActionsProps<TData> {
  selectedCount: number
  selectedRows: TData[]
  actionLabel?: string
  actionLoading?: boolean
  onAction?: (rows: TData[]) => Promise<void> | void

  actions?: DataTableSelectionAction<TData>[]
  onClearSelection?: () => void
}

export function DataTableRowActions<TData>({
  selectedCount,
  selectedRows,
  actionLabel,
  actionLoading = false,
  onAction,
  actions,
  onClearSelection,
}: DataTableRowActionsProps<TData>) {
  const t = useTranslations("site.ui")
  const inFlight = useRef(new Set<number>())
  const [pending, setPending] = useState<Set<number>>(new Set())
  const resolvedActions: DataTableSelectionAction<TData>[] =
    actions && actions.length > 0
      ? actions
      : onAction && actionLabel
        ? [
            {
              label: actionLabel,
              onAction,
              loading: actionLoading,
              variant: "destructive",
            },
          ]
        : []

  async function runAction(action: DataTableSelectionAction<TData>, index: number) {
    if (inFlight.current.has(index) || action.loading || action.disabled) return
    inFlight.current.add(index)
    try {
      const result = action.onAction(selectedRows)
      if (result && typeof result.then === "function") {
        setPending(current => new Set(current).add(index))
        await result
      }
    } finally {
      inFlight.current.delete(index)
      setPending(current => {
        if (!current.has(index)) return current
        const next = new Set(current)
        next.delete(index)
        return next
      })
    }
  }

  if (selectedCount === 0) {
    return <div className="flex-1" />
  }

  return (
    <div className="flex flex-1 items-center gap-4">
      <span className="text-sm text-muted-foreground">
        {t("selectedCount", { count: selectedCount })}
      </span>
      {resolvedActions.map((action, index) => (
        <button
          key={`${action.label}-${index}`}
          type="button"
          className={cn(

            "text-sm text-muted-foreground transition-colors disabled:pointer-events-none disabled:opacity-50",
            action.variant === "destructive"
              ? "hover:text-destructive"
              : "hover:text-primary",
          )}
          disabled={action.loading || action.disabled || pending.has(index)}

          onClick={() => void runAction(action, index)}
        >
          {action.label}
        </button>
      ))}
      {onClearSelection ? (
        <button
          type="button"
          className="text-sm text-muted-foreground transition-colors hover:text-primary"
          onClick={onClearSelection}
        >
          {t("clearSelection")}
        </button>
      ) : null}
    </div>
  )
}
